import { cn } from '@liza/ui/utils';
import { useTranslation } from 'react-i18next';
import { DiffPart } from './NoteDiffPart';
import { ADDED_CLASS, REMOVED_CLASS, type NoteDiffPartView } from './noteDiffClasses';

/** Mirrors `NoteDiffSegment` from `@liza/services` — see `NoteDiffPart` for why it is not imported. */
export type NoteDiffSegmentView = {
  kind: 'unchanged' | 'added' | 'removed' | 'changed';
  value: string;
  parts?: NoteDiffPartView[];
};

export type NoteDiffViewProps = {
  /** Oldest-to-newest ordered segments, as the API returns them. */
  segments: NoteDiffSegmentView[];
};

/**
 * A note diff, rendered inline.
 *
 * Inline rather than side by side: the encounter drawer is a bottom panel 200-400px tall, and two
 * columns of prose there means two short scrollers in which a reader loses their place in both.
 *
 * Rendered as preformatted text rather than through the markdown renderer the note itself uses.
 * Marked-up spans inside markdown would be reflowed or swallowed by the formatter, and a reader
 * looking at a diff is reading *changes* rather than the finished document — the source form is the
 * honest one to mark up.
 */
export const NoteDiffView = ({ segments }: NoteDiffViewProps): React.ReactNode => {
  const { t } = useTranslation('records');

  return (
    <div className="min-h-0 flex-1 overflow-y-auto" data-testid="note-diff">
      {/* Legend first: the colours mean nothing until named, and a clinician may be seeing this once. */}
      <p
        className="mb-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground"
        data-testid="note-diff-legend"
      >
        <span className="flex items-center gap-1.5">
          <span aria-hidden data-legend-swatch className={cn('inline-block size-2.5 rounded-[2px]', ADDED_CLASS)} />
          {t('noteHistoryAdded')}
        </span>
        <span className="flex items-center gap-1.5">
          <span aria-hidden data-legend-swatch className={cn('inline-block size-2.5 rounded-[2px]', REMOVED_CLASS)} />
          {t('noteHistoryRemoved')}
        </span>
      </p>
      <pre className="font-sans text-sm wrap-anywhere whitespace-pre-wrap" data-phi>
        {segments.map((segment, index) => {
          const key = `${index}-${segment.kind}`;
          if (segment.kind === 'changed') {
            return (
              <span key={key} data-testid="note-diff-changed">
                {(segment.parts ?? []).map((part, partIndex) => (
                  <DiffPart key={`${key}-${partIndex}`} part={part} />
                ))}
              </span>
            );
          }
          if (segment.kind === 'unchanged') return <span key={key}>{segment.value}</span>;
          // A structural newline still has to break the line even when it was added or removed —
          // marking it instead swallows the paragraph break and collapses the sections around it.
          if (segment.value.trim() === '') return <span key={key}>{segment.value}</span>;
          return <DiffPart key={key} part={{ kind: segment.kind, value: segment.value }} />;
        })}
      </pre>
    </div>
  );
};
